import { useState, useEffect } from 'react'
import axios from 'axios'
import { Upload, FileText, Trash2, AlertCircle, CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react'

const API_BASE = (import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000') + '/api/v1'

interface Document {
    id: string
    filename: string
    dataset_id: string
    status: 'UPLOADING' | 'INDEXED' | 'FAILED' | 'DELETED'
    total_tokens: number
    chunks_count: number
    created_at: string
    updated_at: string
}

export function KnowledgeTab() {
    const [documents, setDocuments] = useState<Document[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [dragActive, setDragActive] = useState(false)

    const fetchDocuments = async () => {
        setIsLoading(true)
        setError(null)
        try {
            const token = localStorage.getItem('auth_token')
            if (!token) {
                setError('Autenticação necessária. Por favor faça login.')
                setIsLoading(false)
                return
            }

            const { data } = await axios.get(`${API_BASE}/knowledge/documents`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            })
            setDocuments(data.documents || [])
        } catch (err: any) {
            console.warn('Failed to fetch documents:', err.message)
            setError('Falha ao carregar documentos')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchDocuments()
    }, [])

    const handleFileSelect = (file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase()
        const allowedExts = ['pdf', 'txt', 'md']

        if (!ext || !allowedExts.includes(ext)) {
            setError(`Tipo de arquivo inválido. Permitidos: ${allowedExts.join(', ')}`)
            return
        }

        const maxSize = 2 * 1024 * 1024 // 2MB
        if (file.size > maxSize) {
            setError('Arquivo muito grande. Máximo: 2MB')
            return
        }

        setSelectedFile(file)
        setError(null)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        setDragActive(false)

        const file = e.dataTransfer.files[0]
        if (file) handleFileSelect(file)
    }

    const handleUpload = async () => {
        if (!selectedFile) return

        setIsUploading(true)
        setError(null)

        try {
            const formData = new FormData()
            formData.append('file', selectedFile)

            await axios.post(`${API_BASE}/knowledge/upload`, formData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            })

            setSelectedFile(null)
            fetchDocuments()

        } catch (err: any) {
            const errorMsg = err.response?.data?.detail || err.message
            setError(errorMsg)
        } finally {
            setIsUploading(false)
        }
    }

    const handleDelete = async (docId: string) => {
        if (!confirm('Tem certeza que deseja deletar este documento?')) return

        try {
            await axios.delete(`${API_BASE}/knowledge/documents/${docId}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            })
            fetchDocuments()
        } catch (err: any) {
            setError('Falha ao deletar documento')
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'INDEXED': return <CheckCircle2 className="w-4 h-4 text-green-400" />
            case 'UPLOADING': return <Clock className="w-4 h-4 text-blue-400 animate-pulse" />
            case 'FAILED': return <XCircle className="w-4 h-4 text-red-400" />
            default: return <AlertCircle className="w-4 h-4 text-gray-400" />
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'INDEXED': return 'text-green-400 bg-green-500/10 border-green-500/20'
            case 'UPLOADING': return 'text-blue-400 bg-blue-500/10 border-blue-500/20'
            case 'FAILED': return 'text-red-400 bg-red-500/10 border-red-500/20'
            default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20'
        }
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Header */}
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <FileText className="w-6 h-6 text-purple-500" />
                    Base de Conhecimento
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                    Gerencie documentos indexados no RAG (MVP - Pipeline de Embedding em Desenvolvimento)
                </p>
            </div>

            {/* Upload Section */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Upload de Documento</h3>

                {/* Drag & Drop Zone */}
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-white/20 bg-black/20'
                        }`}
                >
                    <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-300 mb-2">
                        Arraste um arquivo ou clique para selecionar
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                        Formatos: PDF, TXT, MD • Tamanho máximo: 2MB
                    </p>

                    <input
                        type="file"
                        accept=".pdf,.txt,.md"
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                        className="hidden"
                        id="file-upload"
                    />
                    <label
                        htmlFor="file-upload"
                        className="inline-block px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition-colors"
                    >
                        Selecionar Arquivo
                    </label>
                </div>

                {/* Selected File */}
                {selectedFile && (
                    <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-blue-400" />
                            <div>
                                <p className="text-white font-medium">{selectedFile.name}</p>
                                <p className="text-xs text-gray-500">
                                    {(selectedFile.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleUpload}
                            disabled={isUploading}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                'Upload'
                            )}
                        </button>
                    </div>
                )}

                {/* Error Display */}
                {error && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2 text-red-300 text-sm">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}
            </div>

            {/* Documents List */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-white">Documentos Indexados</h3>
                    <button
                        onClick={fetchDocuments}
                        disabled={isLoading}
                        className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                        {isLoading ? 'Carregando...' : 'Atualizar'}
                    </button>
                </div>

                {documents.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p>Nenhum documento indexado ainda</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {documents.map((doc) => (
                            <div
                                key={doc.id}
                                className="p-4 bg-white/5 border border-white/10 rounded-lg hover:bg-white/[0.07] transition-colors flex items-center justify-between"
                            >
                                <div className="flex items-center gap-4 flex-1">
                                    <FileText className="w-5 h-5 text-purple-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-medium truncate">{doc.filename}</p>
                                        <div className="flex items-center gap-3 mt-1">
                                            <span className={`text-xs px-2 py-0.5 rounded border flex items-center gap-1 ${getStatusColor(doc.status)}`}>
                                                {getStatusIcon(doc.status)}
                                                {doc.status}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleDelete(doc.id)}
                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                    title="Deletar documento"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* MVP Notice */}
            <div className="mt-6 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                    <div className="text-sm text-yellow-200/80">
                        <p className="font-medium text-yellow-300 mb-1">MVP - Funcionalidade Parcial</p>
                        <p>
                            O pipeline completo de parsing, chunking e embedding ainda está em desenvolvimento.
                            Documentos são armazenados mas não estão disponíveis para busca semântica ainda.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
